import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import {
  REASONS_BY_ANCHOR,
  type SupportReportAnchor,
  type SupportReportReason,
  type SupportSeverity,
  type SupportTicketDetail,
} from "@compliance-sage/contracts";
import { api } from "../../api/client";
import { Modal } from "../../components/Modal";
import { Button, ErrorBanner, Field, TextArea } from "../../components/ui";
import { useMutation } from "../../hooks/useMutation";
import { useI18n } from "../../i18n";
import { AttachedContextPanel } from "./AttachedContextPanel";

const SEVERITIES: SupportSeverity[] = ["BLOCKING", "DEGRADED", "MINOR"];

/**
 * A labelled set of one-click choices. Not `Field`, which renders a <label>:
 * a label wrapping several buttons is associated with the first one, which
 * takes the whole group's text as its accessible name.
 */
function ChoiceGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div role="group" aria-label={label}>
      <span className="mb-1 block text-sm font-medium text-slate-600">{label}</span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

/** The report kind follows the anchor, so the reporter never classifies it. */
function kindFor(anchor: SupportReportAnchor, reason: SupportReportReason | null) {
  return anchor === "ANALYSIS_FAILURE" || reason === "ANALYSIS_FAILED"
    ? ("SYSTEM_ERROR" as const)
    : ("DISPUTED_RESULT" as const);
}

/**
 * Reporting one AI result. The reporter has already told us which result they
 * mean by clicking next to it, and the server derives the review's stage, run,
 * frozen source set, and rule version from the ids — so the only thing left to
 * ask is which of a handful of causes applies. One click sends it.
 */
export function ReportResultDialog({
  open,
  onClose,
  anchor,
  reviewId,
  findingId,
  checklistReferenceId,
  subject,
  anchorLabel,
}: {
  open: boolean;
  onClose: () => void;
  anchor: SupportReportAnchor;
  reviewId: string;
  findingId?: string;
  checklistReferenceId?: string;
  /** Derived from the anchored result, never typed. */
  subject: string;
  /** A short badge line naming what is being reported, e.g. the verdict. */
  anchorLabel?: string;
}) {
  const { t, te } = useI18n();
  const [reason, setReason] = useState<SupportReportReason | null>(null);
  const [severity, setSeverity] = useState<SupportSeverity>(
    anchor === "ANALYSIS_FAILURE" ? "BLOCKING" : "DEGRADED",
  );
  const [note, setNote] = useState("");
  const [created, setCreated] = useState<SupportTicketDetail | null>(null);

  const submit = useMutation(
    useCallback(
      () =>
        api.createSupportTicket({
          kind: kindFor(anchor, reason),
          severity,
          // AI-generated titles can run past the contract's 200-char subject.
          subject: subject.slice(0, 200),
          observed: note.trim() || undefined,
          reason: reason ?? undefined,
          anchor,
          reviewId,
          findingId,
          checklistReferenceId,
          pagePath: window.location.pathname,
        }),
      [anchor, reason, severity, subject, note, reviewId, findingId, checklistReferenceId],
    ),
    t("errSupportSubmit"),
  );

  const close = () => {
    // Reset so reopening does not resurrect a sent report.
    setCreated(null);
    setReason(null);
    setSeverity(anchor === "ANALYSIS_FAILURE" ? "BLOCKING" : "DEGRADED");
    setNote("");
    submit.setError(null);
    onClose();
  };

  const send = async () => {
    const ticket = await submit.mutate();
    if (ticket) setCreated(ticket);
  };

  if (created) {
    return (
      <Modal
        open={open}
        onClose={close}
        size="md"
        title={t("supportSubmitted")}
        footer={
          <Button variant="secondary" onClick={close}>
            {t("close")}
          </Button>
        }
      >
        <p className="text-sm text-slate-600">
          {t("supportSubmittedHelp", { reference: created.reference })}
        </p>
        <Link
          to={`/support/${created.id}`}
          onClick={close}
          className="mt-3 inline-block text-sm font-medium text-brand-700 hover:text-brand-900"
        >
          {created.subject} →
        </Link>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      title={t("reportResultTitle")}
      description={t("reportResultHelp")}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            {t("cancel")}
          </Button>
          <Button onClick={() => void send()} disabled={reason === null || submit.loading}>
            {submit.loading ? t("loading") : t("supportSubmit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ErrorBanner message={submit.error} />

        {/* What is being reported, so the reporter can confirm they clicked the
            right result before sending. */}
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
            {te("supportReportAnchor", anchor)}
          </p>
          <p className="mt-1 break-words text-sm font-medium text-slate-700">{subject}</p>
          {anchorLabel && <p className="mt-0.5 text-xs text-slate-500">{anchorLabel}</p>}
        </div>

        <ChoiceGroup label={t("reportResultReasonLabel")}>
          {REASONS_BY_ANCHOR[anchor].map((value) => {
            const selected = reason === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={selected}
                onClick={() => setReason(value)}
                className={`rounded-full border px-3 py-1.5 text-sm transition ${
                  selected
                    ? "border-brand-500 bg-brand-50 font-medium text-brand-800"
                    : "border-slate-300 text-slate-600 hover:border-brand-400 hover:bg-brand-50/50"
                }`}
              >
                {te("supportReportReason", value)}
              </button>
            );
          })}
        </ChoiceGroup>

        <ChoiceGroup label={t("supportSeverityLabel")}>
          {SEVERITIES.map((value) => {
            const selected = severity === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={selected}
                onClick={() => setSeverity(value)}
                className={`rounded-md border px-3 py-1.5 text-sm transition ${
                  selected
                    ? "border-brand-500 bg-brand-50 font-medium text-brand-800"
                    : "border-slate-300 text-slate-600 hover:border-brand-400 hover:bg-brand-50/50"
                }`}
              >
                {te("supportSeverity", value)}
              </button>
            );
          })}
        </ChoiceGroup>

        <Field label={t("reportResultNoteLabel")}>
          <TextArea
            value={note}
            maxLength={4000}
            placeholder={t("reportResultNotePlaceholder")}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>

        <AttachedContextPanel
          reviewId={reviewId}
          findingId={findingId}
          checklistReferenceId={checklistReferenceId}
        />
      </div>
    </Modal>
  );
}
